import { Box, Divider, Grid, styled, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Typography } from "@mui/material";
import React, { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setLoading } from "../../features/authSlice";
import LoadingSpinner from "../../common/components/LoadingSpinner";
import theme from "../../theme";
import { Close } from "@mui/icons-material";
import CommonButton from "../../common/components/CommonButton";
import { showNotification } from "../../common/components/Notification/ToastifyNotification";
import { adminResetPassword, blockingUser, getUserList } from "../../api/authApi";
import CommonDialog from "../../common/components/CommonDialog";
import CommonFormField from "../../common/components/CommonFormField";
import { LoginType } from "../../common/const";

const Cell = styled(TableCell)(({ theme }) => ({
    fontWeight: 600,
    padding: 5,
    textAlign: 'center',
    fontSize: 13,
    borderRight: `1px solid ${theme.palette.background.secondary}`,
    background: theme.palette.secondary.main,
    color: theme.palette.primary.main
}));

const InnerCell = styled(TableCell)(({ theme }) => ({
    padding: 10,
    textAlign: 'center',
    fontSize: 14,
    color: theme.palette.background.dark,
    borderRight: `1px solid ${theme.palette.background.secondary}`,
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: 1,
    height: 'auto', // Let it adjust based on content
    maxHeight: '600px', // Fixed maximum height
    overflow: 'auto', // Enable scrolling
}));

const ManageUsers = () => {
    const dispatch = useDispatch();
    const { loading } = useSelector((state) => state.auth);
    const [users, setUsers] = useState(null);
    const [pageNo, setPageNo] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [resetDialog, setResetDialog] = useState(false);
    const [formData, setFormData] = useState({
        password: '',
        confirmpassword: ''
    });
    const [errors, setErrors] = useState({});
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [userId, setUserId] = useState(null);

    const validateField = (name, value, allValues = formData) => {
        switch (name) {
            case 'password':
                if (!value) return 'Password is required';
                if (value.length < 8) return 'Password must be at least 8 characters';
                if (!/[A-Z]/.test(value)) return 'Password must contain at least one uppercase letter';
                if (!/[a-z]/.test(value)) return 'Password must contain at least one lowercase letter';
                if (!/\d/.test(value)) return 'Password must contain at least one number';
                if (!/[!@#$%^&*(),.?":{}|<>]/.test(value)) return 'Password must contain at least one special character';
                return '';
            case 'confirmpassword':
                if (!value) return 'Confirm password is required';
                if (value !== allValues.password) return 'Passwords do not match';
                return '';
            default:
                return '';
        }
    };

    const validateForm = () => {
        const newErrors = {};

        const passwordError = validateField('password', formData.password);
        if (passwordError) newErrors.password = passwordError;

        const confirmPasswordError = validateField('confirmpassword', formData.confirmpassword);
        if (confirmPasswordError) newErrors.confirmpassword = confirmPasswordError;

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));

        const error = validateField(name, value, { ...formData, [name]: value });

        if (error) {
            setErrors(prev => ({ ...prev, [name]: error }));
        } else {
            setErrors(prev => ({ ...prev, [name]: undefined }));
        }

        if (name === 'password' && formData.confirmpassword) {
            const confirmError = validateField('confirmpassword', formData.confirmpassword, { ...formData, [name]: value });
            if (confirmError) {
                setErrors(prev => ({ ...prev, confirmpassword: confirmError }));
            } else {
                setErrors(prev => ({ ...prev, confirmpassword: undefined }));
            }
        }
    };

    const handleBlur = (e) => {
        const { name, value } = e.target;
        const error = validateField(name, value);
        if (error) {
            setErrors(prev => ({ ...prev, [name]: error }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (!validateForm()) return;
            dispatch(setLoading({
                loading: true
            }));
            const response = await adminResetPassword({
                userId: userId,
                newPassword: formData.password,
                confirmPassword: formData.confirmpassword
            });
            if (response.success) {
                showNotification(response.message, 'success');
                dispatch(setLoading({
                    loading: false
                }));
                setShowPassword(false);
                setShowConfirmPassword(false);
                setFormData({
                    password: '',
                    confirmpassword: ''
                });
                setUserId(null);
                setResetDialog(false);
            }
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            dispatch(setLoading({
                loading: false
            }));
            showNotification(errorMessage, 'error');
        }
    };

    const getUserData = async () => {
        try {
            dispatch(setLoading({
                loading: true
            }));
            const response = await getUserList(pageNo + 1, pageSize, '');
            setUsers(response.data);
            dispatch(setLoading({
                loading: false
            }));
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    }

    useEffect(() => {
        getUserData();
    }, [pageNo, pageSize]);


    const handleChangePage = (event, newPage) => {
        setPageNo(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setPageSize(parseInt(event.target.value, 10));
        setPageNo(0);
    };

    const handleBlock = async (user) => {
        try {
            dispatch(setLoading({
                loading: true
            }));
            const response = await blockingUser(user.user_id, {
                "status": user.status === 'ACTIVE' ? 'INACTIVE' : "ACTIVE"
            });
            if (response.success) {
                showNotification(response.message, 'success');
                dispatch(setLoading({
                    loading: false
                }));
                setDeleteConfirm(null);
                getUserData();
            }
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    };

    return (
        <Fragment>
            {loading && (
                <LoadingSpinner />
            )}
            <CommonDialog
                open={!!deleteConfirm}
                size="xs"
                content={(
                    <center>
                        <Typography sx={{ fontWeight: 600, fontSize: '22px', mt: 0.5, mb: 1 }}>
                            Are you sure you want to block this user?
                        </Typography>
                    </center>
                )}
                actionButtons={(
                    <Fragment>
                        <CommonButton
                            label="Yes"
                            size="small"
                            background={theme.palette.secondary.main}
                            color={theme.palette.primary.main}
                            handleClick={async () => {
                                handleBlock(deleteConfirm)
                            }}
                        />
                        <CommonButton
                            label="No"
                            size="small"
                            background={theme.palette.background.main}
                            border={`1px solid ${theme.palette.background.light}`}
                            handleClick={() => {
                                setDeleteConfirm(null)
                            }}
                        />
                    </Fragment>
                )}
            />
            <CommonDialog
                open={resetDialog}
                size="sm"
                padding="0px"
                title={
                    <Fragment>
                        <Box display="flex" justifyContent="space-between" alignItems="center" p={2}>
                            <Typography sx={{ fontWeight: 600, fontSize: 18 }}>Reset Password</Typography>
                            <Close
                                sx={{
                                    color: theme.palette.background.dark, fontSize: 20,
                                    float: 'right',
                                    cursor: 'pointer'
                                }}
                                onClick={() => {
                                    setFormData({
                                        password: '',
                                        confirmpassword: ''
                                    });
                                    setShowPassword(false);
                                    setShowConfirmPassword(false);
                                    setUserId(null);
                                    setResetDialog(false);
                                }}
                            />
                        </Box>
                        <Divider />
                    </Fragment>
                }
                content={
                    <Grid container spacing={2} mt={0.1} mb={1}>
                        <Grid item md={12}>
                            <CommonFormField
                                label="Password"
                                type="password"
                                name="password"
                                value={formData.password}
                                onChange={handleInputChange}
                                onBlur={handleBlur}
                                placeholder="•••••••••"
                                error={errors.password}
                                showPassword={showPassword}
                                onTogglePassword={() => setShowPassword(prev => !prev)}
                            />
                        </Grid>

                        <Grid item md={12}>
                            <CommonFormField
                                label="Confirm Password"
                                type="password"
                                name="confirmpassword"
                                value={formData.confirmpassword}
                                onChange={handleInputChange}
                                onBlur={handleBlur}
                                placeholder="•••••••••"
                                error={errors.confirmpassword}
                                showPassword={showConfirmPassword}
                                onTogglePassword={() => setShowConfirmPassword(prev => !prev)}
                            />
                        </Grid>
                    </Grid>
                }
                actionButtons={(
                    <CommonButton
                        label='Update Password'
                        size="small"
                        background={theme.palette.secondary.main}
                        color={theme.palette.background.main}
                        fontWeight={700}
                        marginBottom="10px"
                        handleClick={(e) => {
                            handleSubmit(e);
                        }}
                    />
                )}
            />
            <Box display="flex" justifyContent="space-between">
                <Typography fontSize={20} fontWeight={600}>User Management</Typography>
            </Box>
            <Box pt={1} pb={0}>
                <StyledTableContainer>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: theme.palette.grey[100] }}>
                                <Cell>Student Name</Cell>
                                <Cell>Email Address</Cell>
                                <Cell>User Type</Cell>
                                <Cell>Login Type</Cell>
                                <Cell>Account Status</Cell>
                                <Cell>Subscription Status</Cell>
                                <Cell width={'25%'}>Actions</Cell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {users?.data.length > 0 ? users?.data?.map((user, index) => (
                                <TableRow key={index}>
                                    <InnerCell>
                                        {`${user.first_name} ${user.last_name ? user?.last_name : ''}`}
                                    </InnerCell>
                                    <InnerCell style={{ color: 'black' }}>
                                        {user.email}
                                    </InnerCell>
                                    <InnerCell>
                                        {user.userType?.name}
                                    </InnerCell>
                                    <InnerCell>
                                        {user?.login_type}
                                    </InnerCell>
                                    <InnerCell>
                                        {user.status}
                                    </InnerCell>
                                    <InnerCell>
                                        -
                                    </InnerCell>
                                    <InnerCell sx={{ display: 'flex', gap: 1 }}>
                                        <CommonButton
                                            label={user.status === 'ACTIVE' ? 'Block' : 'Unblock'}
                                            marginBottom="0px"
                                            height="30px"
                                            width="70px"
                                            color="white"
                                            background={user.status === 'ACTIVE' ? "red" : 'green'}
                                            handleClick={() => {
                                                if (user.status === 'ACTIVE') {
                                                    setDeleteConfirm(user)
                                                } else {
                                                    handleBlock(user);
                                                }
                                            }}
                                        />
                                        {user?.login_type === LoginType.EMAIL && (
                                            <CommonButton
                                                label="Reset Password"
                                                marginBottom="0px"
                                                height="30px"
                                                width="140px"
                                                color="white"
                                                background={theme.palette.secondary.main}
                                                handleClick={() => {
                                                    setResetDialog(true);
                                                    setUserId(user?.user_id);
                                                }}
                                            />
                                        )}
                                        <CommonButton
                                            label='Manage Subscription'
                                            marginBottom="0px"
                                            height="30px"
                                            width="180px"
                                            color="white"
                                            background={theme.palette.secondary.main}
                                        />
                                    </InnerCell>
                                </TableRow>
                            )) : (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">
                                        <Typography>No Users Found</Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </StyledTableContainer>
            </Box>

            {/* Pagination Component */}
            <Box sx={{ borderTop: `1px solid ${theme.palette.grey[300]}` }}>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 15, 20, 25, 50]}
                    component="div"
                    count={users?.pagination?.total}
                    rowsPerPage={pageSize}
                    page={pageNo}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    sx={{
                        '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                            fontSize: '0.875rem',
                        },
                    }}
                />
            </Box>
        </Fragment>
    )
}

export default ManageUsers;